import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Lock } from "lucide-react";
import { WheelSVG } from "./WheelSVG";
import { formatBerries, formatRate } from "../game/formatters";
import { getUpgradeCost } from "../game/constants";

const CornerBrackets = ({ color = "#FFD400" }) => (
  <>
    <span
      aria-hidden
      className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2"
      style={{ borderColor: color }}
    />
    <span
      aria-hidden
      className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2"
      style={{ borderColor: color }}
    />
    <span
      aria-hidden
      className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2"
      style={{ borderColor: color }}
    />
    <span
      aria-hidden
      className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2"
      style={{ borderColor: color }}
    />
  </>
);

export const UpgradeCard = ({ upgrade, count, berries, onBuy }) => {
  const cost = getUpgradeCost(upgrade.baseCost, count);
  const affordable = berries >= cost;
  const owned = count > 0;
  // "Locked" = never bought AND cannot afford AND berries less than half cost
  const locked = !owned && berries < cost * 0.5;

  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35 }}
      whileHover={affordable ? { y: -2 } : undefined}
      onClick={() => affordable && onBuy(upgrade.id)}
      disabled={!affordable}
      data-testid={`upgrade-buy-${upgrade.id}`}
      className={`group relative w-full text-left flex items-stretch gap-3 sm:gap-4 p-3 sm:p-4 border transition-colors ${
        affordable
          ? "border-[#FFD400] cursor-pointer active:scale-[0.98]"
          : "border-[#1e1e1e] cursor-not-allowed"
      }`}
      style={{
        backgroundColor: "#0d0d0d",
        borderRadius: 4,
      }}
    >
      {/* Corner brackets on affordable state */}
      {affordable && <CornerBrackets />}

      {/* Hidden nested-button-safe: no nested interactive elements */}

      {/* Icon container */}
      <div
        className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 p-1.5 sm:p-2 flex items-center justify-center ${
          locked ? "opacity-30" : owned ? "opacity-100" : "opacity-70"
        }`}
        style={{
          backgroundColor: "#111",
          border: `1px solid ${owned || affordable ? "#FFD400" : "#232323"}`,
          borderRadius: 2,
        }}
      >
        <WheelSVG eraKey={upgrade.eraKey} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between gap-2">
            <h3
              className={`text-sm sm:text-base font-black uppercase tracking-widest ${
                locked ? "text-white/50" : "text-white"
              } truncate`}
              style={{ fontFamily: "Orbitron, sans-serif" }}
              data-testid={`upgrade-name-${upgrade.id}`}
            >
              {upgrade.name}
            </h3>
            {locked ? (
              <Lock size={14} className="text-white/40 flex-shrink-0" />
            ) : (
              <ChevronRight
                size={16}
                color="#FFD400"
                className="flex-shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform"
              />
            )}
          </div>
          <p
            className={`text-xs mt-1 leading-snug line-clamp-2 ${
              locked ? "text-white/30" : "text-white/60"
            }`}
          >
            {upgrade.description}
          </p>
        </div>

        <div className="mt-2 flex items-end justify-between gap-2">
          <span
            className={`text-[10px] uppercase tracking-[0.25em] font-bold ${
              locked ? "text-white/30" : ""
            }`}
            style={{
              fontFamily: "Orbitron, sans-serif",
              color: locked ? undefined : "#FFD400",
            }}
          >
            +{formatRate(upgrade.production)} Berry/s
          </span>

          {/* Price pill */}
          <div
            className="flex items-center gap-1.5 px-3 py-1 flex-shrink-0"
            style={{
              border: `1px solid ${affordable ? "#FFD400" : "#2a2a2a"}`,
              backgroundColor: affordable ? "rgba(255, 212, 0, 0.08)" : "transparent",
              borderRadius: 2,
            }}
            data-testid={`upgrade-cost-pill-${upgrade.id}`}
          >
            <span
              className={`text-sm font-black ${
                affordable ? "text-white" : "text-white/50"
              }`}
              style={{ fontFamily: "Orbitron, sans-serif" }}
              data-testid={`upgrade-cost-${upgrade.id}`}
            >
              {formatBerries(cost)}
            </span>
            <span
              aria-hidden
              className="inline-block w-3 h-3 rounded-full flex-shrink-0"
              style={{
                backgroundColor: affordable ? "#FFD400" : "#2a2a2a",
                boxShadow: affordable ? "0 0 6px #FFD400" : "none",
              }}
            />
          </div>
          {count > 0 && (
            <span
              className="text-[10px] font-black flex-shrink-0"
              style={{ color: "#FFD400", fontFamily: "Orbitron, sans-serif" }}
              data-testid={`upgrade-count-${upgrade.id}`}
            >
              x{count}
            </span>
          )}
        </div>
      </div>

    </motion.button>
  );
};

export default UpgradeCard;
